/**
 * License Activity Modal - User Timeline Version
 * Simplified version without user information section
 */

import { useEffect, memo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Alert,
} from '@mui/material'
import { Close, Badge as LicenseIcon } from '@mui/icons-material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useAppDispatch } from 'src/store/hooks'
import {
  successMessageChanged,
  errorMessageChanged,
} from 'src/v3/manage/manage.slice'
import CopyToClipboardV4 from 'src/v3/manage/components/copy-to-clipboard'
import type { RosterLicenseActivityData } from '../../types'
import NotificationSnackbars from './notification-snackbars'
import {
  getActivityStatusColor,
  getActivityStatusIcon,
  getActivityStatusLabel,
} from '../../utils/activity-status-utils'

dayjs.extend(relativeTime)

interface LicenseActivityModalProps {
  event: RosterLicenseActivityData | null
  isOpen: boolean
  onClose: () => void
  linkedServiceID: string
}

let LicenseActivityModal = memo(
  ({ event, isOpen, onClose }: LicenseActivityModalProps) => {
    let dispatch = useAppDispatch()

    // Clear messages when modal closes
    useEffect(() => {
      if (!isOpen) {
        dispatch(successMessageChanged(''))
        dispatch(errorMessageChanged(''))
      }
    }, [isOpen, dispatch])

    if (!event) {
      return null
    }

    // Format event data for display
    let displayData = [
      {
        key: 'License Type',
        value: event.licenseName,
        type: 'text',
        isCopyable: false,
      },
      {
        key: 'License ID',
        value: event.licenseID,
        type: 'text',
        isCopyable: true,
      },
      {
        key: 'License Number',
        value: event.licenseNumber,
        type: 'text',
        isCopyable: false,
      },
      {
        key: 'Issuing State',
        value: event.licenseState,
        type: 'text',
        isCopyable: false,
      },
      {
        key: 'Action Date',
        value: event.actionDate,
        type: 'datetime',
        isCopyable: false,
      },
      {
        key: 'Expiration Date',
        value: event.expiration,
        type: 'date',
        isCopyable: false,
      },
      {
        key: 'Days to Expiration',
        value: event.daysToExpiration,
        type: 'number',
        isCopyable: false,
      },
    ].filter((item) => item.value !== undefined && item.value !== null)

    let formatDisplayValue = (value: any, type: string): string => {
      if (!value) return 'N/A'

      switch (type) {
        case 'datetime':
          return dayjs(value).format('MMM D, YYYY [at] h:mm A')
        case 'date':
          return dayjs(value).format('MMM D, YYYY')
        case 'number':
          if (typeof value === 'number') {
            if (value < 0) {
              return `${Math.abs(value)} days expired`
            }
            return `${value} days remaining`
          }
          return String(value)
        default:
          return String(value)
      }
    }

    return (
      <Dialog
        open={isOpen}
        onClose={onClose}
        aria-labelledby="license-activity-modal-title"
        slotProps={{
          paper: {
            sx: {
              width: '80vw',
              height: '80vh',
              maxWidth: 'none',
              maxHeight: 'none',
              borderRadius: 2,
              bgcolor: 'background.default',
            },
          },
        }}
      >
        <DialogTitle>
          <Box className="flex items-center justify-between">
            <Box className="flex items-center gap-4">
              <Typography variant="h6" id="license-activity-modal-title">
                License Activity
              </Typography>
              <Chip
                label={getActivityStatusLabel(
                  event.actionType,
                  event.daysToExpiration,
                  'license',
                )}
                color={getActivityStatusColor(event.actionType) as any}
                icon={getActivityStatusIcon(event.actionType)}
                sx={{
                  fontWeight: 500,
                  '&.MuiChip-root': {
                    backgroundColor: (theme) => {
                      let colorKey = getActivityStatusColor(
                        event.actionType,
                      ) as 'error' | 'warning' | 'success'
                      return `${theme.palette[colorKey].main}20`
                    },
                    color: (theme) => {
                      let colorKey = getActivityStatusColor(
                        event.actionType,
                      ) as 'error' | 'warning' | 'success'
                      return theme.palette[colorKey].main
                    },
                  },
                }}
              />
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="Close dialog"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className="p-4">
          <Box className="mb-4">
            <Typography variant="body2" color="text.secondary" className="mb-1">
              {dayjs(event.actionDate).format('MMMM D, YYYY [at] h:mm A')} •{' '}
              {dayjs(event.actionDate).fromNow()}
            </Typography>
            <Typography variant="body2" className="font-medium">
              {event.licenseName} License{' '}
              {event.licenseNumber && `(${event.licenseNumber})`}
            </Typography>
          </Box>

          {event.actionType === 'expired' && (
            <Alert severity="error" className="mb-6" role="alert">
              <Typography variant="body2" className="font-medium">
                This license has expired
              </Typography>
              <Typography variant="body2">
                Expired {Math.abs(event.daysToExpiration || 0)} days ago. Please
                renew immediately.
              </Typography>
            </Alert>
          )}

          {event.actionType === 'expiring-soon' && (
            <Alert severity="warning" className="mb-6" role="alert">
              <Typography variant="body2" className="font-medium">
                License expiring soon
              </Typography>
              <Typography variant="body2">
                This license expires in {event.daysToExpiration} day
                {event.daysToExpiration === 1 ? '' : 's'}. Please renew before
                expiration.
              </Typography>
            </Alert>
          )}

          <Paper sx={{ p: 2, bgcolor: 'background.paperDark' }}>
            <Typography variant="h6" className="mb-4 flex items-center gap-2">
              <LicenseIcon />
              License Details
            </Typography>

            <Box className="grid grid-cols-1 gap-1 sm:grid-cols-2">
              {displayData.map(({ key, value, type, isCopyable }) => (
                <Box key={key} className="p-2">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    className="block mb-1"
                  >
                    {key}
                  </Typography>
                  {isCopyable ? (
                    <CopyToClipboardV4 value={String(value)} label={key} />
                  ) : (
                    <Typography variant="body2" className="font-medium">
                      {formatDisplayValue(value, type)}
                    </Typography>
                  )}
                </Box>
              ))}

              {event.expiration && (
                <Box className="p-2">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    className="block"
                  >
                    Expiration Status
                  </Typography>
                  <Chip
                    label={
                      event.actionType === 'expired'
                        ? 'Expired'
                        : event.actionType === 'expiring-soon'
                          ? 'Expiring Soon'
                          : 'Active'
                    }
                    color={getActivityStatusColor(event.actionType) as any}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              )}
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions className="p-4 gap-2">
          <Button onClick={onClose} autoFocus>
            Close
          </Button>
        </DialogActions>
        <NotificationSnackbars />
      </Dialog>
    )
  },
)

export default LicenseActivityModal
